import * as React from "react";

import { Check } from "@labxchange/icons";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { MultiSelectQuestionOption, CheckboxState } from "./types";
import {
  optionsGroupClass,
  optionWrapperClass,
  optionTileClass,
  optionTileSelectedClass,
  optionTileCorrectClass,
  optionTileIncorrectClass,
  optionTileDisabledClass,
  checkboxClass,
  checkboxCorrectClass,
  checkboxIncorrectClass,
  checkboxLabelClass,
  checkboxLabelCorrectClass,
  checkboxLabelIncorrectClass,
  optionTextClass,
  optionTextSelectedClass,
  optionTextCorrectClass,
  optionTextIncorrectClass,
  optionImageClass,
  optionImageFullWidthClass,
  checkIconClass,
  feedbackTextClass,
} from "./MultiSelectQuestion.css";

interface CheckboxGroupProps {
  options: MultiSelectQuestionOption[];
  selectedOptionIds: number[];
  submittedOptionIds: number[];
  onOptionToggle: (optionId: number) => void;
  getCheckboxState: (optionId: number) => CheckboxState;
  ariaLabelledby: string;
  resetCount: number;
  isPredictionQuestion: boolean;
  disabled: boolean;
  /** Instance-unique prefix so per-option text/feedback ids don't collide
   * across multiple questions on the same page. */
  idPrefix?: string;
}

const getOptionLetter = (index: number): string => {
  return String.fromCharCode(65 + index); // A, B, C, D, ...
};

const STATE_CLASS_MAP = {
  tile: {
    base: optionTileClass,
    selected: optionTileSelectedClass,
    correct: optionTileCorrectClass,
    incorrect: optionTileIncorrectClass,
    disabled: optionTileDisabledClass,
  },
  checkbox: {
    base: checkboxClass,
    correct: checkboxCorrectClass,
    incorrect: checkboxIncorrectClass,
  },
  checkboxLabel: {
    base: checkboxLabelClass,
    correct: checkboxLabelCorrectClass,
    incorrect: checkboxLabelIncorrectClass,
  },
  optionText: {
    base: optionTextClass,
    selected: optionTextSelectedClass,
    correct: optionTextCorrectClass,
    incorrect: optionTextIncorrectClass,
  },
};

const getClassName = (
  element: keyof typeof STATE_CLASS_MAP,
  state: CheckboxState
) => {
  const config = STATE_CLASS_MAP[element];
  const stateClass =
    state !== "default" ? config[state as keyof typeof config] : undefined;
  return [config.base, stateClass].filter(Boolean).join(" ");
};

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  selectedOptionIds,
  submittedOptionIds,
  onOptionToggle,
  getCheckboxState,
  ariaLabelledby,
  resetCount,
  isPredictionQuestion,
  disabled,
  idPrefix,
}) => {
  const idScope = idPrefix ? `${idPrefix}-` : "";
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent, optionId: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOptionToggle(optionId);
      }
    },
    [onOptionToggle]
  );

  return (
    <div
      role="group"
      aria-labelledby={ariaLabelledby}
      className={optionsGroupClass}
    >
      {options.map((option, index) => {
        const state = getCheckboxState(option.id);
        const isSelected = selectedOptionIds.includes(option.id);
        const letter = getOptionLetter(index);
        const showFeedback = submittedOptionIds.includes(option.id);

        return (
          <div
            key={`${option.id}-${resetCount}`}
            className={optionWrapperClass}
            data-name="Multi-select option"
          >
            <div
              className={getClassName("tile", state)}
              onClick={() => onOptionToggle(option.id)}
              onKeyDown={(e) => handleKeyDown(e, option.id)}
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={disabled}
              aria-labelledby={`${idScope}option-${option.id}-text`}
              aria-describedby={
                showFeedback && option.feedback
                  ? `${idScope}option-${option.id}-feedback`
                  : undefined
              }
              tabIndex={disabled ? -1 : 0}
              data-testid={`option-${option.id}`}
              data-name="tile"
              data-state={state}
            >
              {!isPredictionQuestion && (
                <div
                  className={getClassName("checkbox", state)}
                  data-name="Multi-select checkbox"
                >
                  {state === "correct" ? (
                    <Check
                      size={18}
                      strokeWidth={3}
                      className={checkIconClass}
                    />
                  ) : (
                    <p className={getClassName("checkboxLabel", state)}>
                      {letter}
                    </p>
                  )}
                </div>
              )}
              <div
                id={`${idScope}option-${option.id}-text`}
                className={getClassName("optionText", state)}
              >
                <RichText value={option.text} equation={option.equation} />
                {option.image && (
                  <img
                    src={option.image.src}
                    alt={option.image.altText}
                    className={
                      option.image.fullWidth
                        ? optionImageFullWidthClass
                        : optionImageClass
                    }
                  />
                )}
              </div>
            </div>
            {showFeedback && option.feedback && (
              <p
                className={feedbackTextClass}
                id={`${idScope}option-${option.id}-feedback`}
              >
                <RichText value={option.feedback} />
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
